import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';

import { listCountries, setProvincesList } from 'Containers/Address/actions';
import { CountryModel } from 'Containers/Address/CountryAutocomplete/Models/CountryModel';

import { CountryAutocomplete } from 'Components/Address';

// set country value if passed from the parent (redux variable)
const getCountryLongName = (countriesList: Array<CountryModel>, value: string) => {
  if (value.length === 3) {
    return countriesList.find(
      (country: CountryModel) => country.alpha_3.toLocaleLowerCase() === value.toLocaleLowerCase()
    );
  }
  if (value.length === 2) {
    return countriesList.find(
      (country: CountryModel) => country.alpha_2.toLocaleLowerCase() === value.toLocaleLowerCase()
    );
  }
  return undefined;
};

const countriesSelector = ({ address: { countries } }: any) => countries;

interface Props {
  country: string;
  setSelectedCountry: (e: any) => void;
  invalid?: boolean;
  showCaretIcon?: boolean;
  isContainerVisible?: boolean;
}

const CountryAutocompleteContainer = ({
  country,
  setSelectedCountry,
  invalid,
  showCaretIcon,
  isContainerVisible,
}: Props) => {
  const dispatch = useDispatch();

  const [countryLocal, setCountryLocal] = useState('');
  const [countries, setCountries] = useState([]);
  const [showDropDown, setShowDropDown] = useState(false);

  // API data
  const countriesList: Array<CountryModel> = useSelector(countriesSelector, areEqual);
  useEffect(() => {
    if (isContainerVisible && countriesList.length === 0) {
      dispatch(listCountries());
    }
  }, [isContainerVisible]);

  // we'll use this to prevent the if condition being running onSelectItem useCallback
  const [clear, setClear] = useState(true);
  useEffect(() => {
    // if the country field sent, we'll set the local country state and provinces
    // this happen when a user choose an address from google
    if (country && countriesList.length > 0 && clear) {
      let provinces = [];
      const lowercase = country.toLocaleLowerCase();

      // autopopulate the provinces list based on country selection
      if (country.length === 3) {
        // 3 letters entered
        // might be a country code
        setCountryLocal(getCountryLongName(countriesList, country)?.name);

        // if the country code has provinces associated with it, then populate the provinces/states dropdown
        const foundProvinces = countriesList.find(
          (country: CountryModel) => country.alpha_3.toLocaleLowerCase() === lowercase
        );

        if (foundProvinces) {
          const { provinces: provinceList } = foundProvinces;
          provinces = provinceList;
        }
      } else if (country.length === 2) {
        // as above, might be a country code
        setCountryLocal(getCountryLongName(countriesList, country)?.name);

        const foundProvinces = countriesList.find(
          (country: CountryModel) => country.alpha_2.toLocaleLowerCase() === lowercase
        );

        if (foundProvinces) {
          const { provinces: provinceList } = foundProvinces;
          provinces = provinceList;
        }
      } else {
        // handles anything else entered

        // country selected from Google autocomplete or user typed in the full name
        setCountryLocal(country);

        provinces = countriesList.find(
          (country: CountryModel) => country.name.toLocaleLowerCase() === lowercase
        )?.provinces;
      }

      if (provinces) {
        dispatch(setProvincesList(provinces));
      }
    }
  }, [country, countriesList]);

  useEffect(() => {
    if (countriesList.length > 0) {
      setCountries(countriesList);
    }
  }, [countriesList]);

  const onChangeCountry = useCallback(
    (e: any) => {
      e.preventDefault();

      setClear(false);
      const { value } = e.target;

      if (value.length > 0) {
        const lowercase = value.toLocaleLowerCase();
        const countries = countriesList.filter((country: CountryModel) =>
          country.name.toLocaleLowerCase().includes(lowercase)
        );
        setCountries(countries);
        setShowDropDown(countries.length > 0);
      } else {
        dispatch(setProvincesList([]));
      }
      setSelectedCountry(value);
      setCountryLocal(value);
    },
    [countriesList]
  );

  const onSelectItem = useCallback(
    (e: any) => {
      e.preventDefault();

      const { id = '', name = '' } = e.target.dataset;

      setCountryLocal(name);
      setSelectedCountry(name);

      const { provinces } = countriesList.find((country: CountryModel) => country.id.toString() === id);

      if (provinces) {
        dispatch(setProvincesList(provinces));
      }

      setShowDropDown(false);

      // set this to false, so the if condition in the first useEffect will fail
      setClear(false);
    },
    [countriesList]
  );

  const onClickCaretIcon = useCallback(() => {
    setShowDropDown((prevState) => !prevState);
  }, []);

  // we'll close the dropdown onBlur event outside the dropdown
  // const onBlur = useCallback((e: any) => {
  //   e.preventDefault();
  //   setShowDropDown(false);
  // }, []);

  return (
    <CountryAutocomplete
      country={countryLocal}
      onChangeCountry={onChangeCountry}
      countries={countries}
      onSelectItem={onSelectItem}
      onClickCaretIcon={onClickCaretIcon}
      showCaretIcon={showCaretIcon}
      showDropDown={showDropDown}
      invalid={invalid}
    />
  );
};

CountryAutocompleteContainer.defaultProps = {
  invalid: false,
  showCaretIcon: false,
  isContainerVisible: true,
};

const CountryAutocompleteContainerMemo = memo(CountryAutocompleteContainer, areEqual);

export { CountryAutocompleteContainerMemo as CountryAutocompleteContainer };
